<template>
  <main class="OmnisearchWrapper">
    <div class="mdc-layout-grid GridInContainer">
      <div class="mdc-layout-grid__inner">
        <section class="mdc-layout-grid__cell--span-4-desktop mdc-layout-grid__cell--span-8-tablet mdc-layout-grid__cell--span-4-phone">
          <div class="FilterContent">
            <Filters />
          </div>
        </section>
        <section class="mdc-layout-grid__cell--span-8-desktop  mdc-layout-grid__cell--span-8-tablet mdc-layout-grid__cell--span-4-phone">
          <div class="PageContent">
            <slot />
          </div>
        </section>
      </div>
    </div>
  </main>
  <MobileModalProvider />
</template>

<script setup lang="ts">
import Filters from '@components/Filters/Filters.vue';
import MobileModalProvider from '@components/MobileModal/MobileModalProvider.vue';
import { onMounted } from 'vue';
import { useSearchStore } from '@stores/useSearchStore';
import { useMetaVariablesWatcher } from '@app/composables/useMetaVariablesWatcher';

const searchStore = useSearchStore();

// Load page data on server-side render
if (import.meta.server) await searchStore.loadPageData();

if (import.meta.client) {
	onMounted(() => {
		if (!searchStore.pageData) return;
			searchStore.trackSiteSearch();
  });
}

useMetaVariablesWatcher();

</script>

<style scoped lang="scss">
  .LoadingToggle,
  .RefreshButton {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 0.95rem;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    background: #ffffff;
    color: #111827;
    font-weight: 600;
    box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
    cursor: pointer;
    transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
  }

  .LoadingToggle:hover,
  .RefreshButton:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
    border-color: #d1d5db;
  }

  .LoadingToggle:active,
  .RefreshButton:active {
    transform: translateY(0);
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.14);
  }

  .LoadingToggle__dot {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 9999px;
    background: #9ca3af;
    box-shadow: 0 0 0 4px rgba(156, 163, 175, 0.2);
    transition: background 120ms ease, box-shadow 120ms ease;

    &.is-on {
      background: #16a34a;
      box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.18);
    }
  }

  .RefreshButton {
    background: #111827;
    color: #f9fafb;
    border-color: #0f172a;
    box-shadow: 0 6px 24px rgba(15, 23, 42, 0.22);
  }

  .RefreshButton:hover {
    border-color: #1f2937;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.26);
  }

  .RefreshButton:disabled {
    cursor: not-allowed;
    background: #1f2937;
    border-color: #1f2937;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
    transform: none;
  }

  .RefreshButton__pulse {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 9999px;
    background: #34d399;
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.25);
    transition: box-shadow 200ms ease, background 120ms ease;
  }

  .RefreshButton__pulse.is-busy {
    background: #22c55e;
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.28);
  }

  .ManualFilters {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .ManualFilters label {
    font-weight: 600;
    font-size: 0.9rem;
  }

  .ManualFilters textarea {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 0.6rem;
    font-family: monospace;
    font-size: 0.85rem;
    resize: vertical;
    min-height: 6rem;
    background: #f9fafb;
  }

  .ManualFilters__actions {
    display: flex;
    gap: 0.5rem;
  }

  .ManualFilters__apply,
  .ManualFilters__reset {
    flex: 1;
    padding: 0.55rem 0.75rem;
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    cursor: pointer;
    font-weight: 600;
  }

  .ManualFilters__apply {
    background: #2563eb;
    color: #ffffff;
    border-color: #1d4ed8;
  }

  .ManualFilters__error {
    color: #b91c1c;
    font-size: 0.85rem;
  }

  .OmnisearchWrapper {
    background-color: $Grey-100;
    z-index: $FirstFloor;
    color: $Grey-900;
    min-height: 100vh;

    .FilterContent {
      display: none;

      @include Breakpoint(Large, ExtraLarge) {
        display: block;
        width: 100%;
        height: fit-content;
      }
    }

    .PageContent {
      width: 100%;
    }

    .GridInContainer {
      margin: 0 auto;
      max-width: 79.5rem;
    }
  }

  .MobileFiltersToggle {
    display: flex;
    justify-content: flex-end;
    margin: 0 auto 1rem;
    max-width: 79.5rem;
    width: 100%;

    @include PaddingAreas(0, 1);

    @include Breakpoint(Large, ExtraLarge) {
      display: none;
    }

    &__button {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      border-radius: 0.5rem;
      border: 1px solid $Grey-300;
      background: $White;
      color: $Grey-900;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(16, 24, 40, 0.1);
      transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 18px rgba(16, 24, 40, 0.14);
        border-color: $Grey-400;
      }

      &:active {
        transform: translateY(0);
        box-shadow: 0 6px 12px rgba(16, 24, 40, 0.12);
      }
    }
  }
</style>
